/**
 * Runtime config for the admin console. Leaner even than the support
 * console's: this app has no Grafana deep-links and no per-site
 * bootstrap data at all -- the site list IS the data it fetches, so
 * baking sites into config would be circular.
 *
 * Not shared with `apps/support-console`'s copy despite the near-identical
 * shape. Sharing would mean one `ConsoleRuntimeConfig` type serving two
 * apps with different OIDC clients, different redirect URIs and
 * different admitted roles, and the first field either one needed
 * exclusively would put it in the other's config too. The duplicated
 * surface here is ~40 lines of validation; the security-sensitive parts
 * (role extraction, token storage, return-to sanitisation) are the ones
 * that are shared, and those are shared exactly.
 */
import type { AuthMode } from '@shared/auth/types';

export interface ConsoleZitadelConfig {
  issuer: string;
  clientId: string;
  scopes: string[];
  redirectUri: string;
  postLogoutRedirectUri: string;
  audience?: string;
}

export interface AdminRuntimeConfig {
  authMode: AuthMode;
  env: 'local' | 'staging' | 'production';
  zitadel: ConsoleZitadelConfig;
  /** Always Envoy's front door, so edge-authz's platform_admin-only
   *  check on `/api/v1/admin/` is never bypassed. See vite.config.ts
   *  for the same rule in local dev. */
  apiBaseUrl: string;
}

let cached: AdminRuntimeConfig | null = null;

export function setRuntimeConfig(cfg: AdminRuntimeConfig): void {
  cached = cfg;
}

export function getRuntimeConfig(): AdminRuntimeConfig {
  if (!cached) {
    throw new Error('Runtime config not loaded. Call loadRuntimeConfig() during bootstrap.');
  }
  return cached;
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

export function validateRuntimeConfig(cfg: AdminRuntimeConfig): void {
  const errors: string[] = [];
  if (!cfg || typeof cfg !== 'object') {
    throw new Error('Invalid runtime-config.json:\n - config root must be an object');
  }
  if (cfg.authMode !== 'zitadel' && cfg.authMode !== 'mock') {
    errors.push(`authMode must be 'zitadel' or 'mock' (got '${String(cfg.authMode)}')`);
  }
  // Same rule as both other SPAs' validators, and it matters most
  // here: a leaked authMode=mock config on the console that can
  // destroy a site's live data must never be usable outside env=local.
  if (cfg.authMode === 'mock' && cfg.env !== 'local') {
    errors.push(`authMode=mock is not permitted when env='${cfg.env}' (only env='local')`);
  }
  if (!isNonEmptyString(cfg.apiBaseUrl)) {
    errors.push('apiBaseUrl must be a non-empty string');
  }
  if (cfg.authMode === 'zitadel') {
    if (!isNonEmptyString(cfg.zitadel?.issuer)) errors.push('zitadel.issuer must be a non-empty string');
    if (!isNonEmptyString(cfg.zitadel?.clientId)) errors.push('zitadel.clientId must be a non-empty string');
  }
  if (errors.length > 0) {
    throw new Error(`Invalid runtime-config.json:\n - ${errors.join('\n - ')}`);
  }
}

export async function loadRuntimeConfig(): Promise<AdminRuntimeConfig> {
  const resp = await fetch('/config/runtime-config.json', { cache: 'no-store' });
  if (!resp.ok) {
    throw new Error(`Failed to load runtime-config.json: HTTP ${resp.status}`);
  }
  const cfg = (await resp.json()) as AdminRuntimeConfig;
  validateRuntimeConfig(cfg);
  setRuntimeConfig(cfg);
  return cfg;
}
